import { useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { snackbar, useConfirmDialog } from '@occc-app/design-system';
import { AudienceActivateMutation } from '@occc/graphql';
import {
  useAudienceActivateMutation,
  useAudienceDeleteMutation,
} from '@occc-app/dataAccessLayer/audience/mutations';
import { useChainId } from '@occc-app/Chain/hooks';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import { useUserSelector } from '@occc-app/App/AppContext';
import { AudienceDetailsPage } from './AudienceDetailsPage';
import { prepareAudienceAttributes } from './utils';
import { useGetAudienceWithDataById } from '../../../dataAccessLayer/audience/useGetAudienceWithDataById';
import { AUDIENCE_EXPORT_DEFAULT_NAME } from '../components/messages';
import { AudienceActionConfirmDialog } from '../components/AudienceActionConfirmDialog';
import { ExportOptionsDialog } from '../components/AudienceExportDialog';
import { routeCreator } from '../utils';
import { archiveAudienceError, archiveAudienceSuccess } from '../copies';

type AudienceActionType = 'activate' | 'archive' | 'unarchive';

export const AudienceDetailsPageContainer = () => {
  const chainId = useChainId();
  const navigate = useNavigate();
  const { id: audienceId = '' } = useParams<{ id: string }>();
  const user = useUserSelector();
  const { loading, error, data: audience } = useGetAudienceWithDataById(audienceId, chainId);
  const [audienceActionType, setAudienceActionType] = useState<AudienceActionType>('activate');
  const [isExportDialogOpen, setExportDialogOpen] = useState(false);
  const isUserPermittedToEditAudience = useIsUserPermittedTo(Feature.AUDIENCE_EDIT);
  const isUserPermittedToExportAudience = useIsUserPermittedTo(Feature.AUDIENCE_EXPORT);
  const isUserPermittedToArchiveAudience = useIsUserPermittedTo(Feature.AUDIENCE_ARCHIVE);
  const isUserPermittedToActivateAudience = useIsUserPermittedTo(Feature.AUDIENCE_ACTIVATE);

  const {
    open: isConfirmationOpen,
    setOpen: setConfirmationOpen,
    onCloseButtonClick,
  } = useConfirmDialog();

  const onDeleteCompleted = useCallback(() => {
    setConfirmationOpen(false);
    snackbar.success(archiveAudienceSuccess);
    navigate(routeCreator.base());
  }, [navigate, setConfirmationOpen]);

  const onDeleteError = useCallback(() => snackbar.error(archiveAudienceError), []);

  const onActivateCompleted = useCallback(
    (mutation: AudienceActivateMutation) => {
      setConfirmationOpen(false);
      snackbar.success(`Audience ${mutation.audienceActivate.name} is successfully activated.`);
    },
    [setConfirmationOpen],
  );

  const [audienceDeleteMutation, { loading: deleteLoading }] = useAudienceDeleteMutation({
    variables: { uuid: audienceId },
    onCompleted: onDeleteCompleted,
    onError: onDeleteError,
    refetchQueries: ['audiences'],
  });
  const [audienceActivateMutation, { loading: activateLoading }] = useAudienceActivateMutation({
    variables: { uuid: audienceId },
    onCompleted: onActivateCompleted,
    refetchQueries: ['audience'],
  });

  // Previously, the type was described as "Audience | undefined" but it's not correct.
  // "any" is used here as a temporary solution and should be replaced later with something more specific.
  const preparedAudience: any = audience
    ? {
        ...audience,
        targetingAttributes: prepareAudienceAttributes(audience.targetingAttributes),
      }
    : undefined;

  return (
    <>
      <AudienceDetailsPage
        loading={loading}
        error={error}
        audience={preparedAudience}
        onActivate={() => {
          setConfirmationOpen(true);
          setAudienceActionType('activate');
        }}
        onArchive={() => {
          setConfirmationOpen(true);
          setAudienceActionType('archive');
        }}
        onUnarchive={() => {
          setConfirmationOpen(true);
          setAudienceActionType('unarchive');
        }}
        onExport={() => setExportDialogOpen(true)}
        isUserPermittedToEditAudience={isUserPermittedToEditAudience}
        isUserPermittedToExportAudience={isUserPermittedToExportAudience}
        isUserPermittedToArchiveAudience={isUserPermittedToArchiveAudience}
        isUserPermittedToActivateAudience={isUserPermittedToActivateAudience}
      />

      <AudienceActionConfirmDialog
        open={isConfirmationOpen}
        actionType={audienceActionType}
        loading={activateLoading || deleteLoading}
        audienceName={audience.name ?? ''}
        onClose={onCloseButtonClick}
        onActivate={audienceActivateMutation}
        onArchive={audienceDeleteMutation}
        onUnarchive={audienceActivateMutation}
      />

      {user?.email && (
        <ExportOptionsDialog
          open={isExportDialogOpen}
          onClose={() => setExportDialogOpen(false)}
          audienceId={audienceId}
          audienceName={audience.name || AUDIENCE_EXPORT_DEFAULT_NAME}
          userEmail={user.email}
        />
      )}
    </>
  );
};
